import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import {
  Landmark,
  RefreshCw,
  Copy,
  CheckCircle2,
  AlertCircle,
  Building2,
  CreditCard,
  QrCode,
  ArrowUpRight,
  ArrowDownLeft,
  ArrowLeft,
  Zap,
  Shield,
  TrendingUp,
  Clock,
  ExternalLink,
  Info
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { 
  providerAPI, 
  balanceAPI, 
  virtualAccountAPI,
  formatINR,
  PROVIDER_LABELS 
} from '@/services/unifiedPayments';

const Banking = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [providerStatus, setProviderStatus] = useState(null);
  const [balance, setBalance] = useState(null);
  const [virtualAccounts, setVirtualAccounts] = useState([]);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchProviderStatus();
    fetchVirtualAccounts();
  }, []);

  const fetchProviderStatus = async () => {
    try {
      const status = await providerAPI.getStatus();
      setProviderStatus(status);
      
      if (status.health?.primary) {
        fetchBalance();
      }
    } catch (err) {
      console.error('Failed to fetch provider status:', err);
      // If we get an error, the provider might not be configured
      setProviderStatus({ 
        active_provider: 'mock', 
        health: { primary: true },
        configured_provider: 'mock'
      });
      // Still try to fetch balance from mock provider
      fetchBalance();
    } finally {
      setLoading(false);
    }
  };

  const fetchBalance = async () => {
    try {
      setRefreshing(true);
      const data = await balanceAPI.getBalance();
      setBalance(data);
    } catch (err) {
      console.error('Failed to fetch balance:', err);
      toast.error('Failed to fetch balance');
    } finally {
      setRefreshing(false);
    }
  };

  const fetchVirtualAccounts = async () => {
    try {
      const accounts = await virtualAccountAPI.list();
      setVirtualAccounts(accounts || []);
    } catch (err) {
      console.error('Failed to fetch virtual accounts:', err);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  const getProviderInfo = () => {
    const provider = providerStatus?.active_provider || 'mock';
    return PROVIDER_LABELS[provider] || PROVIDER_LABELS.mock;
  };

  const isDemo = providerStatus?.active_provider === 'mock';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/admin')}
            data-testid="back-to-dashboard-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Banking</h1>
            <p className="text-gray-500 mt-1">Manage payments, virtual accounts, and bank integrations</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button onClick={fetchBalance} disabled={refreshing} variant="outline">
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button 
            onClick={() => navigate('/admin/initiate-payment')}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="initiate-payment-btn"
          >
            <ArrowUpRight className="w-4 h-4 mr-2" />
            Make Payment
          </Button>
        </div>
      </div>

      {/* Demo Mode Banner */}
      {isDemo && (
        <Alert className="border-amber-200 bg-amber-50">
          <Info className="w-4 h-4 text-amber-600" />
          <AlertDescription className="text-amber-800">
            <strong>Demo Mode Active</strong> - Banking operations are simulated. 
            This is perfect for testing and demonstrating FinOrchest to potential bank partners.
            Real bank integration will be enabled once partnership is established.
          </AlertDescription>
        </Alert>
      )}

      {/* Provider Status Card */}
      <Card data-testid="provider-status-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Landmark className="w-5 h-5 text-blue-600" />
            Payment Provider
          </CardTitle>
          <CardDescription>
            Current banking integration status
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Provider Info */}
            <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                isDemo ? 'bg-amber-100' : 'bg-green-100'
              }`}>
                <Building2 className={`w-5 h-5 ${isDemo ? 'text-amber-600' : 'text-green-600'}`} />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">{getProviderInfo().name}</p>
                <p className="text-xs text-gray-500">{getProviderInfo().description}</p>
              </div>
            </div>

            {/* Health Status */}
            <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                providerStatus?.health?.primary ? 'bg-green-100' : 'bg-red-100'
              }`}>
                {providerStatus?.health?.primary ? (
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-red-600" />
                )}
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">System Status</p>
                <p className={`text-sm ${providerStatus?.health?.primary ? 'text-green-600' : 'text-red-600'}`}>
                  {providerStatus?.health?.primary ? 'Operational' : 'Disconnected'}
                </p>
              </div>
            </div>

            {/* Provider Mode */}
            <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                isDemo ? 'bg-blue-100' : 'bg-green-100'
              }`}>
                <Shield className={`w-5 h-5 ${isDemo ? 'text-blue-600' : 'text-green-600'}`} />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">Mode</p>
                <Badge variant={isDemo ? 'secondary' : 'default'}>
                  {isDemo ? 'Demo / Test' : 'Production'}
                </Badge>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Account Balance */}
      <Card data-testid="account-balance-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-blue-600" />
            Account Balance
          </CardTitle>
        </CardHeader>
        <CardContent>
          {balance ? (
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <p className="text-4xl font-bold text-gray-900">
                  {formatINR(balance.available_balance)}
                </p>
                <p className="text-sm text-gray-500 mt-1 flex items-center gap-2">
                  <Clock className="w-3 h-3" />
                  Available Balance • Updated {new Date(balance.as_of).toLocaleTimeString()}
                  {isDemo && (
                    <Badge variant="outline" className="ml-2 text-xs">Simulated</Badge>
                  )}
                </p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => navigate('/admin/transactions')}>
                  <TrendingUp className="w-4 h-4 mr-2 text-blue-600" />
                  View History
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <p>Unable to fetch balance. Click refresh to try again.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Virtual Accounts */}
      <Card data-testid="virtual-accounts-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <QrCode className="w-5 h-5 text-blue-600" />
                Department Virtual Accounts
              </CardTitle>
              <CardDescription>
                Virtual bank accounts for department collections
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {virtualAccounts.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <QrCode className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Virtual Accounts Yet</h3>
              <p className="text-gray-500 mb-4 max-w-sm mx-auto">
                Virtual accounts are created automatically when you enable them for departments.
              </p>
              <Button 
                variant="outline" 
                onClick={() => navigate('/admin/departments')}
              >
                Go to Departments
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {virtualAccounts.map((va) => (
                <div key={va.va_id} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="font-medium text-gray-900">{va.department_name}</h4>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant={va.status === 'active' ? 'success' : 'secondary'}>
                          {va.status}
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                          {va.provider}
                        </Badge>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Bank Account */}
                    <div className="bg-white border rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-gray-500 uppercase tracking-wide">Bank Account</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyToClipboard(va.account_number, 'Account number')}
                        >
                          <Copy className="w-3 h-3" />
                        </Button>
                      </div>
                      <p className="font-mono text-sm text-gray-900">{va.account_number}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        IFSC: {va.ifsc_code} • {va.bank_name}
                      </p>
                    </div>

                    {/* UPI ID */}
                    {va.upi_id && (
                      <div className="bg-white border rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs text-gray-500 uppercase tracking-wide">UPI ID</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => copyToClipboard(va.upi_id, 'UPI ID')}
                          >
                            <Copy className="w-3 h-3" />
                          </Button>
                        </div>
                        <p className="font-mono text-sm text-gray-900">{va.upi_id}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          Accept payments via any UPI app
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card 
          className="hover:shadow-md transition-shadow cursor-pointer"
          onClick={() => navigate('/admin/initiate-payment')}
        >
          <CardContent className="p-6 flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
              <ArrowUpRight className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h3 className="font-medium text-gray-900">Make Payout</h3>
              <p className="text-sm text-gray-500">Send money to beneficiaries</p>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="hover:shadow-md transition-shadow cursor-pointer"
          onClick={() => navigate('/admin/transactions')}
        >
          <CardContent className="p-6 flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
              <ArrowDownLeft className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <h3 className="font-medium text-gray-900">View Transactions</h3>
              <p className="text-sm text-gray-500">Track all payments</p>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="hover:shadow-md transition-shadow cursor-pointer"
          onClick={() => navigate('/admin/departments')}
        >
          <CardContent className="p-6 flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
              <Building2 className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h3 className="font-medium text-gray-900">Departments</h3>
              <p className="text-sm text-gray-500">Manage department accounts</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* CBDC Future Feature Card */}
      <Card className="border-dashed border-2 border-blue-200 bg-blue-50/50">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
              <Zap className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-1">Coming Soon: Digital Rupee (CBDC)</h3>
              <p className="text-sm text-gray-600 mb-3">
                FinOrchest is designed to be CBDC-ready. When RBI launches enterprise CBDC features,
                you'll be able to leverage programmable money - funds with built-in spending rules,
                time-bound budgets, and automatic compliance.
              </p>
              <a 
                href="/cbdc-landing.html" 
                target="_blank"
                className="text-blue-600 hover:text-blue-700 text-sm font-medium inline-flex items-center gap-1"
              >
                Learn more about our CBDC vision
                <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Banking;
